import React from 'react'
import NextImage from 'next/image'
import {
  Footer,
  ClassyFooter,
  PageHeader,
  ComputedUserTheme,
  Member as BlocksMember,
} from '@classy/campaign-page-blocks'
import countryToCurrency from 'country-to-currency'
import { BlockBuilder } from 'features/Block/BlockBuilder'
import { MemberDropdown } from './MemberDropdown'
import { DenormalizedPageData, PageConfig } from 'models/pages'
import { EVENT } from 'models/event'
import { Member } from 'models/member'
import { getCampaignProgressMetrics } from 'services/campaign'
import { onLogin } from 'services/sso'
import { CartController } from 'features/Cart'
import { logger } from 'utils/logger'
import { getFooter } from 'features/Block/footer/footer'
import { getPageHeader } from 'features/Block/page-header/page-header'
import { GlobalCurrencyControl } from 'features/Passport/GlobalCurrencyControl'
import { Environment } from 'utils/environment'
import { ClientHeadersData } from 'hooks/useClientHeadersData'
import { CurrencyOption, getCurrencyOptionsByCode } from 'services/currency'
import { useCustomEmbeddedCodeManager } from 'features/CustomEmbeddedCodeManager'

export interface CampaignPageProps
  extends Pick<DenormalizedPageData, 'theme' | 'sharedBlocks' | 'pageData'> {
  __ENV?: Environment
  pageConfig: PageConfig
  member?: Member | null
  clientHeadersData?: ClientHeadersData
}

/**
 * This component assumes all campaign pages share the same layout.
 */
export const CampaignPage = ({
  __ENV,
  clientHeadersData,
  pageData,
  sharedBlocks,
  theme,
  member,
  pageConfig,
}: CampaignPageProps) => {
  const pageHeaderProps = getPageHeader(sharedBlocks)?.props
  const footerProps = getFooter(sharedBlocks)?.props
  const [currencies, setCurrencies] = React.useState<CurrencyOption[] | null>(null)

  React.useEffect(() => {
    const getCurrencies = async () => {
      const data = await getCurrencyOptionsByCode(pageConfig.campaignRawCurrencyCode)
      setCurrencies(data)
    }
    getCurrencies()
  }, [pageConfig.campaignRawCurrencyCode])

  /**
   * Find the user's local currency code using their IP address (via Cloudflare)
   */
  const currencyCodeFromIpAddress = clientHeadersData
    ? countryToCurrency[clientHeadersData.geoCountry]
    : pageConfig.campaignRawCurrencyCode

  // Used for A/B testing by Cro Metrics. The SC type name comes from an appv2 convention and stands
  // for StayClassy (the old name of the company).
  React.useEffect(() => {
    window.SC = {
      ab_testing: {
        cid: Number(pageConfig.campaignId),
        oid: Number(pageConfig.orgId),
        donation_type: 'studio',
        campaign_type: 'studio',
        features: pageConfig.isCartEnabled ? ['cart'] : [],
      },
    }
  }, [pageConfig.campaignId, pageConfig.orgId, pageConfig.isCartEnabled])

  useCustomEmbeddedCodeManager({
    pageConfig,
  })

  // * Fetch campaign progress metrics from the search service
  const campaignProgressMetrics = React.useCallback(async () => {
    try {
      if (pageConfig.campaignId) {
        const { donorCount, goal, percentToGoal, totalRaised } = await getCampaignProgressMetrics(
          pageConfig.campaignId,
        )

        // * Dispatch campaign data for interested blocks
        document.dispatchEvent(
          new CustomEvent(EVENT.SEARCH_GET_CAMPAIGN_PROGRESS_METRICS, {
            detail: {
              donorCount,
              goal,
              percentToGoal,
              totalRaised,
            },
          }),
        )
      }
    } catch (e) {
      logger(
        'error',
        new Error('Unable to fetch campaign details from Classy Search', { cause: e }),
        {
          context: { campaignId: pageConfig.campaignId, orgId: pageConfig.orgId },
        },
      )
    }
  }, [pageConfig.campaignId, pageConfig.orgId])

  React.useEffect(() => {
    campaignProgressMetrics()
  }, [campaignProgressMetrics])

  // Member info is a truncated member object for use by page blocks. See GlobalBlockProps for info.
  let memberInfo: BlocksMember | undefined
  if (member) {
    memberInfo = {
      id: String(member.id),
      avatar: member.thumbnail_small,
      canDeleteComments: false,
    }
  }

  const isGfmP2pCampaign = pageConfig.campaignType === 'gfm_p2p'

  const showGlobalCurrencyControl =
    !isGfmP2pCampaign && !pageConfig.isCartEnabled && !pageConfig.isPassportDisabled
  const showCartController =
    pageConfig.isCartEnabled && pageConfig.campaignId && pageConfig.checkoutBaseUrl

  return (
    <>
      <ComputedUserTheme theme={theme} />
      {pageHeaderProps && (
        <PageHeader hidePlaceholder NextImage={NextImage as never} {...pageHeaderProps}>
          {showGlobalCurrencyControl && (
            <GlobalCurrencyControl
              currencies={
                currencies || [
                  {
                    value: pageConfig.campaignRawCurrencyCode,
                    label: pageConfig.campaignRawCurrencyCode,
                  },
                ]
              }
              campaignRawCurrencyCode={pageConfig.campaignRawCurrencyCode}
              currencyCodeFromIpAddress={currencyCodeFromIpAddress}
            />
          )}
          {showCartController && (
            <CartController
              checkoutBaseUrl={pageConfig.checkoutBaseUrl || ''}
              checkoutQueryParams={pageConfig.forwardCheckoutQueryParams || {}}
              campaignId={pageConfig.campaignId}
            />
          )}
          {member && (
            <MemberDropdown
              member={member}
              orgId={pageConfig.orgId}
              classyBaseUri={__ENV?.classyBaseUri}
            />
          )}
        </PageHeader>
      )}
      <BlockBuilder pageData={pageData} member={memberInfo} __ENV={__ENV} />
      <footer>
        {footerProps && <Footer NextImage={NextImage as never} {...footerProps} />}
        <ClassyFooter
          data-id="cp-footer-login"
          isLoggedIn={!!member}
          onLogin={() => onLogin(__ENV as Environment, pageConfig.orgId)}
          privacyLinkUrl="./privacy"
          termsLinkUrl="./terms"
        />
      </footer>
    </>
  )
}
